import { isNumber } from '@fabric-app/utils/guards';
import { numberToRoman, numberToLetter } from '@fabric-app/utils/number';
import { Node, wrappingInputRule, mergeAttributes } from '@tiptap/core';
import { isReplaceStep, isReplaceAroundStep, isAttrStep } from '../utils.js';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { markdownSplitDOMLists } from './FbcList.js';

const numberToFormat = (number, format, depth) => {
  switch (format) {
    case "numbers":
      return number.toString();
    case "letters":
      return numberToLetter(number);
    case "roman":
      return numberToRoman(number).toLocaleLowerCase();
    default: {
      const formats = [
        "numbers",
        "letters",
        "roman"
      ];
      return numberToFormat(number, formats[depth % formats.length], depth);
    }
  }
};
const calculateNumber = (doc, node, pos) => {
  const resolvedPos = doc.resolve(pos);
  const parent = resolvedPos.parent;
  const index = resolvedPos.index();
  const depth = resolvedPos.depth;
  let posStart = pos;
  let totalNumbersBefore = 0;
  let firstNumberSibling = node;
  for (let i = index - 1; i >= 0; i--) {
    const child = parent.child(i);
    if (child.type.name === "fbcNumberList") {
      totalNumbersBefore++;
      firstNumberSibling = child;
      posStart -= child.nodeSize - 2;
    } else {
      break;
    }
  }
  let totalDepth = 0;
  let format = node.attrs.format;
  for (let i = depth; i >= 0; i--) {
    const node2 = resolvedPos.node(i);
    if (node2.type.name === "fbcNumberList") {
      totalDepth++;
      if (node2.attrs.format !== "default" && format === "default") {
        format = node2.attrs.format;
      }
    } else {
      break;
    }
  }
  const startIndex = isNumber(firstNumberSibling?.attrs.start) ? firstNumberSibling.attrs.start : 1;
  const currentNumber = startIndex + totalNumbersBefore;
  return {
    currentNumber,
    format,
    totalDepth,
    posStart: posStart - 4,
    formattedNumber: numberToFormat(currentNumber, format, totalDepth)
  };
};
const inputRegex = /^(\d+)\.\s$/;
const FbcNumberList = Node.create({
  priority: 100,
  name: "fbcNumberList",
  content: "paragraph block*",
  group: "block list",
  addStorage() {
    return {
      markdown: {
        serialize(state, node, parent, index) {
          const number = calculateNumber(parent, node, index);
          return state.renderList(node, "  ", () => `${number.formattedNumber}. `);
        },
        parse: {
          updateDOM: markdownSplitDOMLists("ol")
        }
      }
    };
  },
  addAttributes() {
    return {
      start: {
        default: 1,
        parseHTML: (element) => {
          return element.hasAttribute("data-start") ? element.getAttribute("data-start") : 1;
        },
        renderHTML: (attributes) => {
          return {
            "data-start": attributes.start
          };
        }
      },
      format: {
        default: "default",
        parseHTML: (element) => {
          return element.hasAttribute("data-format") ? element.getAttribute("data-format") : "default";
        },
        renderHTML: (attributes) => {
          return {
            "data-format": attributes.format
          };
        }
      }
    };
  },
  parseHTML() {
    return [
      {
        priority: 52,
        tag: "ol",
        skip: true
      },
      {
        tag: "li",
        priority: 52,
        getAttrs: (node) => {
          const closestList = node.closest("ol,ul");
          if (closestList && closestList.tagName.toLowerCase() === "ol") {
            return {
              ["data-type"]: "fbc-number-list",
              start: closestList.hasAttribute("start") ? closestList.getAttribute("start") : 1
            };
          }
          return false;
        }
      },
      {
        tag: 'div[data-type="fbc-number-list"]'
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "ol",
      mergeAttributes(HTMLAttributes, {
        "data-type": "fbc-number-list"
      }),
      [
        "li",
        0
      ]
    ];
  },
  addNodeView() {
    return ({ editor, node, getPos }) => {
      const { dom, contentDOM, onUpdate } = renderFbcNumberList(node, editor.state.doc, getPos());
      const onEditorUpdate = ({ editor: editor2, transaction }) => onUpdate({
        editor: editor2,
        transaction,
        getPos
      });
      editor.on("update", onEditorUpdate);
      return {
        dom,
        contentDOM,
        destroy: () => {
          editor.off("update", onEditorUpdate);
        }
      };
    };
  },
  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: (match) => ({
          start: +match[1]
        }),
        joinPredicate: (match, node) => node.childCount + node.attrs.start === +match[1]
      })
    ];
  }
});
const renderFbcNumberList = (node, doc, initialPos) => {
  const result = calculateNumber(doc, node, initialPos);
  const formattedNumber = result.formattedNumber;
  let posStart = result.posStart;
  const dom = document.createElement("div");
  dom.classList.add("fbc-list");
  dom.dataset.type = "fbc-number-list";
  dom.dataset.uuid = node.attrs["data-uuid"];
  dom.dataset.createdAt = node.attrs["data-created-at"];
  const prefix = document.createElement("div");
  prefix.contentEditable = "false";
  prefix.classList.add("fbc-list-prefix");
  const prefixContent = document.createElement("div");
  prefixContent.classList.add("fbc-list-prefix-content");
  prefixContent.style.setProperty("--fbc-list-prefix", `"${formattedNumber}."`);
  prefixContent.contentEditable = "false";
  prefix.appendChild(prefixContent);
  const content = document.createElement("div");
  content.classList.add("fbc-list-content");
  dom.append(prefix, content);
  const onUpdate = ({ editor, transaction, getPos }) => {
    try {
      const pos = getPos();
      if (!pos) return;
      const steps = transaction.steps;
      if (steps.length === 0 || !transaction.docChanged) return;
      let changedOrAdjacentChanged = false;
      steps.forEach((step) => {
        if (isReplaceStep(step)) {
          if (step.from >= posStart || step.to >= posStart) changedOrAdjacentChanged = true;
        } else if (isReplaceAroundStep(step)) {
          if (step.from >= posStart || step.to >= posStart) changedOrAdjacentChanged = true;
        } else if (isAttrStep(step)) {
          if (step.pos >= posStart) changedOrAdjacentChanged = true;
        }
      });
      if (!changedOrAdjacentChanged) return;
      const { formattedNumber: formattedNumber2, ...rest } = calculateNumber(editor.state.doc, node, pos);
      if (rest.posStart !== posStart) {
        posStart = rest.posStart;
      }
      prefixContent.style.setProperty("--fbc-list-prefix", `"${formattedNumber2}."`);
    } catch (error) {
      captureException(new Error("Failed to update fbc number list"), {
        originalException: error
      });
    }
  };
  return {
    dom,
    contentDOM: content,
    onUpdate
  };
};

export { FbcNumberList as default, inputRegex, renderFbcNumberList };
//# sourceMappingURL=FbcNumberList.js.map
